import XLSX from 'xlsx-js-style'
import { saveAs } from 'file-saver'
import moment from 'moment'

function s2ab (s) {
  const buf = new ArrayBuffer(s.length)
  const view = new Uint8Array(buf)
  for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF
  return buf
}

const HEADER_FILL = '2E7D32'
const HEADER_FILL_LIGHT = '43A047'
const HEADER_FONT = 'FFFFFF'
const ALTERNATE_FILL = 'F2F7F2'
const BORDER_COLOR = 'CFD8DC'

function buildColumns (groupLabel, groupMap, formatters) {
  const fmtAbsent = (item) => formatters.formatCompactDates(item.absentRecords)
  const fmtNoRecord = (item) => formatters.formatCompactDates(item.noRecordAbsentRecords)
  const fmtLeave = (item) => formatters.formatCompactDates(item.leaveRecords)
  const fmtMissedPunch = (item) => formatters.formatCompactMissedPunch(item.missedPunchRecords)
  const fmtLate = (item) => {
    if (!item.lateHrsRecords || !item.lateHrsRecords.length) return '-'
    return item.lateHrsRecords.map(r => moment(r.date).format('DD') + formatters.formatSingleLateHours(r)).join(', ')
  }
  const fmtPenalty = (item) => {
    if (!item.penaltyHoursRecords || !item.penaltyHoursRecords.length) return '-'
    return item.penaltyHoursRecords.map(r => moment(r.date).format('DD') + formatters.formatSinglePenaltyHours(r)).join(', ')
  }
  const pct = (item) => item.fullExpectedHrs ? `${((item.fullPayHours / item.fullExpectedHrs) * 100).toFixed(2)}%` : '0%'

  return [
    { label: '#', value: (item, idx) => idx + 1, align: 'center' },
    { label: 'Name', value: item => item.name, align: 'left', bold: true },
    { label: groupLabel, value: item => groupMap[item.group] || item.group || '', align: 'left' },
    { label: 'Absent Days', value: item => (item.absentRecords || []).length, align: 'right' },
    { label: 'Absent Days Detail', value: fmtAbsent, align: 'left' },
    { label: 'Appr Mods', value: item => (item.noRecordAbsentRecords || []).filter(r => r.status === 'Approved').length, align: 'right' },
    { label: 'Appr Mods Detail', value: fmtNoRecord, align: 'left' },
    { label: 'Leave Days', value: item => (item.leaveRecords || []).filter(l => l.status === 'Approved').length, align: 'right' },
    { label: 'Leave Days Detail', value: fmtLeave, align: 'left' },
    { label: 'Annual Leave', value: item => item.annualLeaveDays || 0, align: 'right' },
    { label: 'Unpaid Leave', value: item => item.unpaidLeaveDays || 0, align: 'right' },
    { label: 'Other Leaves', value: item => item.otherLeaveDays || 0, align: 'right' },
    { label: 'Missed Punch', value: item => `${(item.missedPunch || 0).toFixed(2)} hrs`, align: 'right' },
    { label: 'Missed Punch Detail', value: fmtMissedPunch, align: 'left' },
    { label: 'Late Hours', value: item => `${(item.lateHrs || 0).toFixed(2)} hrs`, align: 'right' },
    { label: 'Late Hours Detail', value: fmtLate, align: 'left' },
    { label: 'Penalty Hours', value: item => `${(item.penaltyHours || 0).toFixed(2)} hrs`, align: 'right' },
    { label: 'Penalty Hours Detail', value: fmtPenalty, align: 'left' },
    { label: 'Pay Hrs', value: pct, align: 'right' },
  ]
}

function buildTitleRows (colCount, companyHeader, title, dateRangeText) {
  const rows = []
  const merges = []
  const titleRows = []

  const addRow = (text, size) => {
    const row = new Array(colCount).fill('')
    row[0] = text
    const r = rows.length
    rows.push(row)
    merges.push({ s: { r, c: 0 }, e: { r, c: colCount - 1 } })
    titleRows.push({ r, size })
  }

  if (companyHeader) addRow(companyHeader, 14)
  addRow(title, 14)
  addRow(dateRangeText, 11)
  rows.push(new Array(colCount).fill(''))
  return { rows, merges, titleRows }
}

function buildSheet (columns, titleInfo, rows, dataRows, extraMerges) {
  const colCount = columns.length
  const headerRowIdx = rows.length
  rows.push(columns.map(c => c.label))
  dataRows.forEach(r => rows.push(r))

  const dataStart = headerRowIdx + 1

  const ws = XLSX.utils.aoa_to_sheet(rows)
  ws['!merges'] = [...titleInfo.merges, ...(extraMerges || [])]
  ws['!cols'] = [
    { wch: 5 },
    { wch: 25 },
    { wch: 20 },
    { wch: 11 },
    { wch: 30 },
    { wch: 9 },
    { wch: 30 },
    { wch: 10 },
    { wch: 30 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 12 },
    { wch: 30 },
    { wch: 12 },
    { wch: 30 },
    { wch: 12 },
    { wch: 30 },
    { wch: 12 },
  ]

  const range = XLSX.utils.decode_range(ws['!ref'])

  const applyStyle = (addr, style) => {
    if (!ws[addr]) return
    if (!ws[addr].s) ws[addr].s = {}
    ws[addr].s = Object.assign({}, ws[addr].s, style)
  }

  const borderRight = { style: 'thin', color: { rgb: BORDER_COLOR } }
  const borderBottom = { style: 'thin', color: { rgb: BORDER_COLOR } }

  titleInfo.titleRows.forEach(({ r, size: sz }) => {
    for (let c = 0; c < colCount; c++) {
      applyStyle(XLSX.utils.encode_cell({ r, c }), {
        font: { bold: true, sz, color: { rgb: HEADER_FONT } },
        fill: { fgColor: { rgb: HEADER_FILL }, patternType: 'solid' },
        alignment: { horizontal: 'center', vertical: 'center' },
      })
    }
  })

  for (let c = 0; c < colCount; c++) {
    applyStyle(XLSX.utils.encode_cell({ r: headerRowIdx, c }), {
      font: { bold: true, color: { rgb: HEADER_FONT } },
      fill: { fgColor: { rgb: HEADER_FILL_LIGHT }, patternType: 'solid' },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: { right: borderRight },
    })
  }

  const rangeStart = titleInfo.titleRows.length ? dataStart : headerRowIdx + 1

  for (let r = rangeStart; r <= range.e.r; r++) {
    const isAlt = (r - rangeStart) % 2 === 1
    for (let c = 0; c <= range.e.c; c++) {
      const addr = XLSX.utils.encode_cell({ r, c })
      if (!ws[addr]) continue
      const style = {
        alignment: {
          wrapText: true,
          vertical: 'center',
          horizontal: (columns[c] && columns[c].align) || 'left',
        },
      }
      if (isAlt) {
        style.fill = { fgColor: { rgb: ALTERNATE_FILL }, patternType: 'solid' }
        style.border = { right: borderRight, bottom: borderBottom }
      } else {
        style.border = { right: borderRight, bottom: borderBottom }
      }
      if (c === 1 && columns[c] && columns[c].bold) {
        style.font = Object.assign({}, style.font, { bold: true })
      }
      applyStyle(addr, style)
    }
  }

  return ws
}

export function exportDetailTimeToExcel ({
  items,
  groupBy,
  companyHeader,
  title,
  dateRangeText,
  groupLabel,
  groupMap = {},
  formatters,
  filename,
}) {
  const columns = buildColumns(groupLabel, groupMap, formatters)
  const colCount = columns.length
  const wb = XLSX.utils.book_new()
  const safeName = (val) => String(val || 'Sheet').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Sheet'

  if (groupBy) {
    const groupsMap = {}
    items.forEach(item => {
      const key = item.group || 'Ungrouped'
      if (!groupsMap[key]) groupsMap[key] = []
      groupsMap[key].push(item)
    })
    const keys = Object.keys(groupsMap).sort((a, b) => a.localeCompare(b))

    keys.forEach(key => {
      const members = groupsMap[key]
      const groupName = groupMap[key] || key || 'Ungrouped'

      const titleInfo = buildTitleRows(colCount, companyHeader, title, dateRangeText)
      const rows = titleInfo.rows
      const groupTitleRow = new Array(colCount).fill('')
      groupTitleRow[0] = `${groupLabel}: ${groupName}`
      const groupTitleIdx = rows.length
      rows.push(groupTitleRow)
      rows.push(new Array(colCount).fill(''))

      const dataRows = members.map((item, idx) => columns.map(c => c.value(item, idx)))

      const ws = buildSheet(columns, titleInfo, rows, dataRows, [{ s: { r: groupTitleIdx, c: 0 }, e: { r: groupTitleIdx, c: colCount - 1 } }])

      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: groupTitleIdx, c })
        if (!ws[addr]) continue
        ws[addr].s = Object.assign({}, ws[addr].s, {
          font: { bold: true, sz: 12, color: { rgb: HEADER_FONT } },
          fill: { fgColor: { rgb: HEADER_FILL_LIGHT }, patternType: 'solid' },
          alignment: { horizontal: 'left', vertical: 'center' },
        })
      }

      XLSX.utils.book_append_sheet(wb, ws, safeName(groupName))
    })
  } else {
    const titleInfo = buildTitleRows(colCount, companyHeader, title, dateRangeText)
    const rows = titleInfo.rows

    const list = [...items].sort((a, b) => {
      const groupA = a.group || ''
      const groupB = b.group || ''
      const nameA = a.name || ''
      const nameB = b.name || ''
      return groupA.localeCompare(groupB) || nameA.localeCompare(nameB)
    })

    const dataRows = list.map((item, idx) => columns.map(c => c.value(item, idx)))

    const ws = buildSheet(columns, titleInfo, rows, dataRows)
    XLSX.utils.book_append_sheet(wb, ws, safeName('Detail Time'))
  }

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' })
  saveAs(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), `${filename}.xlsx`)
  return true
}
